import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpStatusCode,
  HttpUserEvent,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { authLog } from 'src/app/auth/auth.handler';
import { Api } from 'src/app/core/common/api';
import { AppSettings } from 'src/app/core/common/app.settings';
import { Auth } from 'src/app/core/common/auth';
import { DataServices } from 'src/app/data/data.services';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private auth = inject(Auth);
  private router = inject(Router);
  private api = inject(Api);
  private data = inject(DataServices);
  private appSettings = inject(AppSettings);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<unknown> | HttpUserEvent<unknown>> {
    const isApiUrl = req.url.indexOf(this.api.baseUrl) === 0;
    return this.auth.setAuthHeader(req, isApiUrl).pipe(
      switchMap(request => {
        return next.handle(request).pipe(
          catchError(err => {
            if (err instanceof HttpErrorResponse) {
              switch (err.status as HttpStatusCode) {
                case HttpStatusCode.Unauthorized:
                  return from(this.handleUnauthorized(req.url)).pipe(switchMap(() => throwError(() => err as unknown)));
                case HttpStatusCode.Forbidden:
                  return from(this.handleForbidden(req.url)).pipe(switchMap(() => throwError(() => err as unknown)));
              }
            }
            return throwError(() => err as unknown);
          })
        );
      })
    );
  }
  async handleUnauthorized(url: string) {
    authLog(`auth.flow::=>auth.interceptor.handleUnauthorized: kicking login flow`, { url, route: this.router.url });
    await firstValueFrom(this.auth.login());
  }

  async handleForbidden(url: string) {
    const roles = await firstValueFrom(this.auth.getRoles());
    const isPlaceholder = this.auth.isPlaceholderCustomer(roles);
    authLog(`auth.flow::=>auth.interceptor.handleForbidden:`, { url, route: this.router.url, isPlaceholder });
    if (isPlaceholder) {
      // signed-in user who hasn't completed registration — route to signup to upgrade from PLACEHOLDER_CUSTOMER to CUSTOMER
      this.router.navigate(['/signup']);
      return;
    }
    // non-placeholder forbidden: revoked/stale access (e.g: active staging login session getting used on dev) — clear session and local caches
    try {
      await this.auth.logout();
      await this.data.dispose();
      this.router.navigate(['/tabs/home']);
    } catch {
      // swallow — logout failures shouldn't re-throw into the original request's error stream
    }
  }
}
